/**
 * UranIA PA — Sezione "Utenti del tenant" per la pagina admin tenant detail.
 *
 * Renderizza:
 *   - Tabella utenti (nome, email, ruolo primario, stato attivo)
 *   - CTA "Nuovo utente" → form (nome, cognome, email, password, ruolo)
 *   - Edit inline → form (nome, cognome, email, ruolo, enabled)
 *   - Reset password (modale con nuova password)
 *   - Disabilita / Riabilita (toggle ``enabled``)
 *   - Elimina (hard delete con conferma esplicita)
 */

import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';

import {
  createTenantUser,
  deleteTenantUser,
  listTenantUsers,
  resetTenantUserPassword,
  TenantUsersApiError,
  updateTenantUser,
} from '../../lib/admin/tenant-users-api.ts';
import type {
  AssignableRole,
  CreateTenantUserBody,
  ListTenantUsersDto,
  ResetPasswordBody,
  TenantUserDto,
  UpdateTenantUserBody,
} from '../../lib/admin/tenant-users-api.ts';

const ASSIGNABLE_ROLES: readonly AssignableRole[] = [
  'funzionario_adg',
  'beneficiario_ri',
  'auditor',
  'tenant_admin',
];

interface TenantUsersSectionProps {
  readonly tenantId: string;
}

export function TenantUsersSection(props: TenantUsersSectionProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const queryKey = useMemo(
    () => ['admin', 'tenant-users', props.tenantId] as const,
    [props.tenantId],
  );

  const [mode, setMode] = useState<
    | { kind: 'idle' }
    | { kind: 'creating' }
    | { kind: 'editing'; user: TenantUserDto }
    | { kind: 'resetting'; user: TenantUserDto }
  >({ kind: 'idle' });

  const usersQuery = useQuery<ListTenantUsersDto, TenantUsersApiError>({
    queryKey,
    queryFn: () => listTenantUsers(props.tenantId),
    staleTime: 5_000,
  });

  const deleteMutation = useMutation<void, TenantUsersApiError, string>({
    mutationFn: (userId) => deleteTenantUser(props.tenantId, userId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
    },
  });

  const items = usersQuery.data?.items ?? [];

  return (
    <div
      className="card shadow-sm border-0 mb-3"
      data-testid={`admin-tenant-users-${props.tenantId}`}
    >
      <div className="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
        <span
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          {t('admin.tenant_users.heading', { count: usersQuery.data?.total ?? 0 })}
        </span>
        {mode.kind === 'idle' && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={(): void => setMode({ kind: 'creating' })}
            data-testid="admin-tenant-users-new"
          >
            + {t('admin.tenant_users.actions.new')}
          </button>
        )}
      </div>

      {mode.kind === 'creating' && (
        <div className="card-body border-bottom">
          <CreateUserForm
            tenantId={props.tenantId}
            onSaved={(): void => setMode({ kind: 'idle' })}
            onClose={(): void => setMode({ kind: 'idle' })}
          />
        </div>
      )}

      {mode.kind === 'editing' && (
        <div className="card-body border-bottom">
          <EditUserForm
            tenantId={props.tenantId}
            user={mode.user}
            onSaved={(): void => setMode({ kind: 'idle' })}
            onClose={(): void => setMode({ kind: 'idle' })}
          />
        </div>
      )}

      {mode.kind === 'resetting' && (
        <div className="card-body border-bottom">
          <ResetPasswordForm
            tenantId={props.tenantId}
            user={mode.user}
            onSaved={(): void => setMode({ kind: 'idle' })}
            onClose={(): void => setMode({ kind: 'idle' })}
          />
        </div>
      )}

      {usersQuery.isLoading && (
        <p className="px-3 py-3 mb-0 small text-muted" role="status">
          {t('admin.tenant_users.loading')}
        </p>
      )}

      {usersQuery.isError && (
        <div className="alert alert-danger m-3" role="alert">
          <strong>{t('admin.tenant_users.errors.load_failed')}</strong>
          {usersQuery.error.detail !== null && (
            <p className="mb-0 small">{usersQuery.error.detail}</p>
          )}
        </div>
      )}

      {usersQuery.isSuccess && (
        <div className="table-responsive">
          <table
            className="table table-hover mb-0 align-middle"
            data-testid="admin-tenant-users-table"
          >
            <thead>
              <tr>
                <th>{t('admin.tenant_users.columns.name')}</th>
                <th>{t('admin.tenant_users.columns.email')}</th>
                <th>{t('admin.tenant_users.columns.role')}</th>
                <th>{t('admin.tenant_users.columns.enabled')}</th>
                <th className="text-end">
                  <span className="visually-hidden">
                    {t('admin.tenant_users.columns.actions')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center text-muted small fst-italic py-4"
                  >
                    {t('admin.tenant_users.empty')}
                  </td>
                </tr>
              )}
              {items.map((u) => (
                <tr
                  key={u.user_id}
                  data-testid={`admin-tenant-user-row-${u.user_id}`}
                  style={{ opacity: u.enabled ? 1 : 0.55 }}
                >
                  <td>
                    <strong>
                      {u.first_name} {u.last_name}
                    </strong>
                  </td>
                  <td>
                    <code className="small">{u.email}</code>
                  </td>
                  <td>
                    <RoleBadge role={u.role} />
                  </td>
                  <td>
                    {u.enabled ? (
                      <span
                        className="badge"
                        style={{
                          backgroundColor: '#d1e7dd',
                          color: '#0f5132',
                          fontSize: '0.65rem',
                        }}
                      >
                        {t('admin.tenant_users.status.enabled')}
                      </span>
                    ) : (
                      <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                        {t('admin.tenant_users.status.disabled')}
                      </span>
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group btn-group-sm">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={(): void => setMode({ kind: 'editing', user: u })}
                        data-testid={`admin-tenant-user-edit-${u.user_id}`}
                      >
                        {t('admin.tenant_users.actions.edit')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={(): void =>
                          setMode({ kind: 'resetting', user: u })
                        }
                        data-testid={`admin-tenant-user-reset-${u.user_id}`}
                      >
                        {t('admin.tenant_users.actions.reset_password')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={(): void => {
                          if (
                            window.confirm(
                              t('admin.tenant_users.actions.delete_confirm', {
                                name: `${u.first_name} ${u.last_name}`,
                              }),
                            )
                          ) {
                            deleteMutation.mutate(u.user_id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        data-testid={`admin-tenant-user-delete-${u.user_id}`}
                      >
                        {t('admin.tenant_users.actions.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RoleBadge(props: { readonly role: string }): JSX.Element {
  const { t } = useTranslation();
  const colors: Record<string, { bg: string; fg: string }> = {
    funzionario_adg: { bg: '#cfe2ff', fg: '#084298' },
    beneficiario_ri: { bg: '#d1e7dd', fg: '#0f5132' },
    auditor: { bg: '#fff3cd', fg: '#664d03' },
    tenant_admin: { bg: '#e2d9f3', fg: '#3d0a82' },
  };
  const c = colors[props.role] ?? { bg: '#e9ecef', fg: '#495057' };
  return (
    <span
      className="badge"
      style={{ backgroundColor: c.bg, color: c.fg, fontSize: '0.7rem' }}
    >
      {t(`admin.tenant_users.roles.${props.role}`, { defaultValue: props.role })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Form components
// ---------------------------------------------------------------------------

interface CreateFormProps {
  readonly tenantId: string;
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

function CreateUserForm(props: CreateFormProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const nomeId = useId();
  const cognomeId = useId();
  const emailId = useId();
  const passwordId = useId();
  const roleId = useId();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AssignableRole>('funzionario_adg');

  const mutation = useMutation<TenantUserDto, TenantUsersApiError, CreateTenantUserBody>({
    mutationFn: (body) => createTenantUser(props.tenantId, body),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['admin', 'tenant-users', props.tenantId],
      });
      props.onSaved();
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      password,
      role,
    });
  };

  return (
    <form onSubmit={onSubmit} data-testid="admin-tenant-users-create-form">
      <h3 className="h6 mb-3">{t('admin.tenant_users.form.heading_create')}</h3>
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor={nomeId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.first_name')}
          </label>
          <input
            id={nomeId}
            type="text"
            className="form-control form-control-sm"
            value={firstName}
            onChange={(e): void => setFirstName(e.target.value)}
            required
            maxLength={128}
            data-testid="admin-tenant-users-create-first-name"
          />
        </div>
        <div className="col-md-6">
          <label htmlFor={cognomeId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.last_name')}
          </label>
          <input
            id={cognomeId}
            type="text"
            className="form-control form-control-sm"
            value={lastName}
            onChange={(e): void => setLastName(e.target.value)}
            required
            maxLength={128}
            data-testid="admin-tenant-users-create-last-name"
          />
        </div>
        <div className="col-md-6">
          <label htmlFor={emailId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.email')}
          </label>
          <input
            id={emailId}
            type="email"
            className="form-control form-control-sm"
            value={email}
            onChange={(e): void => setEmail(e.target.value)}
            required
            maxLength={320}
            data-testid="admin-tenant-users-create-email"
          />
        </div>
        <div className="col-md-6">
          <label htmlFor={passwordId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.password')}
          </label>
          <input
            id={passwordId}
            type="password"
            className="form-control form-control-sm"
            value={password}
            onChange={(e): void => setPassword(e.target.value)}
            minLength={8}
            required
            autoComplete="new-password"
            data-testid="admin-tenant-users-create-password"
          />
          <small className="form-text text-muted">
            {t('admin.tenant_users.form.password_hint')}
          </small>
        </div>
        <div className="col-md-6">
          <label htmlFor={roleId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.role')}
          </label>
          <select
            id={roleId}
            className="form-select form-select-sm"
            value={role}
            onChange={(e): void => setRole(e.target.value as AssignableRole)}
            data-testid="admin-tenant-users-create-role"
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`admin.tenant_users.roles.${r}`, { defaultValue: r })}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top gap-2">
        <div className="flex-grow-1">
          {mutation.isError && (
            <div className="text-danger small" role="alert">
              {mutation.error.detail ?? mutation.error.message}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={props.onClose}
        >
          {t('admin.tenant_users.form.cancel')}
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={mutation.isPending}
          data-testid="admin-tenant-users-create-submit"
        >
          {mutation.isPending
            ? t('admin.tenant_users.form.submitting')
            : t('admin.tenant_users.form.submit_create')}
        </button>
      </div>
    </form>
  );
}

interface EditFormProps {
  readonly tenantId: string;
  readonly user: TenantUserDto;
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

function EditUserForm(props: EditFormProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const nomeId = useId();
  const cognomeId = useId();
  const emailId = useId();
  const roleId = useId();
  const enabledId = useId();
  const [firstName, setFirstName] = useState(props.user.first_name);
  const [lastName, setLastName] = useState(props.user.last_name);
  const [email, setEmail] = useState(props.user.email);
  const [role, setRole] = useState<AssignableRole>(
    (ASSIGNABLE_ROLES as readonly string[]).includes(props.user.role)
      ? (props.user.role as AssignableRole)
      : 'funzionario_adg',
  );
  const [enabled, setEnabled] = useState(props.user.enabled);

  const mutation = useMutation<TenantUserDto, TenantUsersApiError, UpdateTenantUserBody>({
    mutationFn: (body) => updateTenantUser(props.tenantId, props.user.user_id, body),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['admin', 'tenant-users', props.tenantId],
      });
      props.onSaved();
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      role,
      enabled,
    });
  };

  return (
    <form onSubmit={onSubmit} data-testid="admin-tenant-users-edit-form">
      <h3 className="h6 mb-3">
        {t('admin.tenant_users.form.heading_edit', {
          name: `${props.user.first_name} ${props.user.last_name}`,
        })}
      </h3>
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor={nomeId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.first_name')}
          </label>
          <input
            id={nomeId}
            type="text"
            className="form-control form-control-sm"
            value={firstName}
            onChange={(e): void => setFirstName(e.target.value)}
            required
            maxLength={128}
          />
        </div>
        <div className="col-md-6">
          <label htmlFor={cognomeId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.last_name')}
          </label>
          <input
            id={cognomeId}
            type="text"
            className="form-control form-control-sm"
            value={lastName}
            onChange={(e): void => setLastName(e.target.value)}
            required
            maxLength={128}
          />
        </div>
        <div className="col-md-6">
          <label htmlFor={emailId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.email')}
          </label>
          <input
            id={emailId}
            type="email"
            className="form-control form-control-sm"
            value={email}
            onChange={(e): void => setEmail(e.target.value)}
            required
            maxLength={320}
          />
        </div>
        <div className="col-md-6">
          <label htmlFor={roleId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.role')}
          </label>
          <select
            id={roleId}
            className="form-select form-select-sm"
            value={role}
            onChange={(e): void => setRole(e.target.value as AssignableRole)}
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`admin.tenant_users.roles.${r}`, { defaultValue: r })}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-12">
          <div className="form-check">
            <input
              id={enabledId}
              type="checkbox"
              className="form-check-input"
              checked={enabled}
              onChange={(e): void => setEnabled(e.target.checked)}
            />
            <label htmlFor={enabledId} className="form-check-label small">
              {t('admin.tenant_users.form.enabled')}
            </label>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top gap-2">
        <div className="flex-grow-1">
          {mutation.isError && (
            <div className="text-danger small" role="alert">
              {mutation.error.detail ?? mutation.error.message}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={props.onClose}
        >
          {t('admin.tenant_users.form.cancel')}
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={mutation.isPending}
          data-testid="admin-tenant-users-edit-submit"
        >
          {mutation.isPending
            ? t('admin.tenant_users.form.submitting')
            : t('admin.tenant_users.form.submit_update')}
        </button>
      </div>
    </form>
  );
}

interface ResetFormProps {
  readonly tenantId: string;
  readonly user: TenantUserDto;
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

function ResetPasswordForm(props: ResetFormProps): JSX.Element {
  const { t } = useTranslation();
  const passwordId = useId();
  const [newPassword, setNewPassword] = useState('');

  const mutation = useMutation<void, TenantUsersApiError, ResetPasswordBody>({
    mutationFn: (body) =>
      resetTenantUserPassword(props.tenantId, props.user.user_id, body),
    onSuccess: () => {
      props.onSaved();
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate({ new_password: newPassword });
  };

  return (
    <form onSubmit={onSubmit} data-testid="admin-tenant-users-reset-form">
      <h3 className="h6 mb-3">
        {t('admin.tenant_users.form.heading_reset', {
          name: `${props.user.first_name} ${props.user.last_name}`,
        })}
      </h3>
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor={passwordId} className="form-label small text-muted mb-1">
            {t('admin.tenant_users.form.new_password')}
          </label>
          <input
            id={passwordId}
            type="password"
            className="form-control form-control-sm"
            value={newPassword}
            onChange={(e): void => setNewPassword(e.target.value)}
            minLength={8}
            required
            autoComplete="new-password"
            data-testid="admin-tenant-users-reset-password"
          />
          <small className="form-text text-muted">
            {t('admin.tenant_users.form.password_hint')}
          </small>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top gap-2">
        <div className="flex-grow-1">
          {mutation.isError && (
            <div className="text-danger small" role="alert">
              {mutation.error.detail ?? mutation.error.message}
            </div>
          )}
          {mutation.isSuccess && (
            <div className="text-success small" role="status">
              {t('admin.tenant_users.form.reset_done')}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={props.onClose}
        >
          {t('admin.tenant_users.form.cancel')}
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={mutation.isPending}
          data-testid="admin-tenant-users-reset-submit"
        >
          {mutation.isPending
            ? t('admin.tenant_users.form.submitting')
            : t('admin.tenant_users.form.submit_reset')}
        </button>
      </div>
    </form>
  );
}
